import { ISelectOption } from '@easylife365/react';
import i18n from 'i18next';
import { CredentialTaskDto, CredentialType, IdentityTaskDto, IdentityTaskStatus, ResourceType } from '../api/app.types';
import { AdditionalInfo, AppBaseDto, KeyCredentialDto, Owner, PasswordCredentialDto } from '../api/graph.types';

export enum ExpirationType {
  AboutToExpire = 'AboutToExpire',
  Expired = 'Expired',
  Managed = 'Managed',
}

export type AppOverviewDto = AppBaseDto & {
  credentialDate: Date | undefined;
  taskStatus?: IdentityTaskStatus;
  taskIsAssigned?: boolean;
  /** Merged in from the activity scan; absent for app registrations and for apps with no recorded sign-in. */
  lastSignInDateTime?: string;
};

export type Credential = {
  credentialType?: CredentialType;
  resourceType?: ResourceType;
  appId?: string;
  appOwners?: Owner[];
  info?: AdditionalInfo | null;
} & Partial<PasswordCredentialDto> &
  Partial<KeyCredentialDto>;

export type MonthTime = number;

// A function, not a constant: the text has to follow the language at the time it is rendered.
export const getTaskStateOptions = (): ISelectOption[] => [
  {
    key: IdentityTaskStatus.Done.toString(),
    text: i18n.t('common:taskStatus.closed'),
  },
  {
    key: IdentityTaskStatus.InProgress.toString(),
    text: i18n.t('common:taskStatus.inProgress'),
  },
  {
    key: IdentityTaskStatus.New.toString(),
    text: i18n.t('common:taskStatus.new'),
  },
];

export function getResourceTypeDisplayName(resourceType: ResourceType) {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return i18n.t('common:resourceType.appRegistration', { count: 1 });

    case ResourceType.EnterpriseApplication:
      return i18n.t('common:resourceType.enterpriseApplication', { count: 1 });
  }
}

export function getAssociatedResourceTypeDisplayName(resourceType: ResourceType) {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return i18n.t('common:resourceType.enterpriseApplication', { count: 1 });

    case ResourceType.EnterpriseApplication:
      return i18n.t('common:resourceType.appRegistration', { count: 1 });
  }
}

export function getCredentialTypeDisplayName(credentialType: CredentialType) {
  switch (credentialType) {
    case CredentialType.Certificate:
      return i18n.t('common:certificate', { count: 1 });

    case CredentialType.Secret:
      return i18n.t('common:secret', { count: 1 });
  }
}

export function getTargetResource(resourceType: ResourceType) {
  switch (resourceType) {
    case ResourceType.AppRegistration:
      return 'registeredApps';

    case ResourceType.EnterpriseApplication:
      return 'apps';
  }
}

export function getTargetCredential(credentialType: CredentialType) {
  switch (credentialType) {
    case CredentialType.Certificate:
      return 'certificates';

    case CredentialType.Secret:
      return 'secrets';
  }
}

export function makePathToCredentialDetails(id: string, resourceType: ResourceType, credentialType: CredentialType) {
  const targetResource = getTargetResource(resourceType);
  const targetCredential = getTargetCredential(credentialType);

  return `/${targetResource}/${id}/${targetCredential}`;
}

export function isSamlApp(app: AppBaseDto) {
  return app.preferredSingleSignOnMode?.toLowerCase() === 'saml';
}

export function joinCredentialTask(
  task: IdentityTaskDto,
  app: AppBaseDto,
  isSaml: boolean,
  credential: PasswordCredentialDto | KeyCredentialDto
): CredentialTaskDto | null {
  return {
    keyId: credential.keyId!,
    appDisplayName: app.displayName,
    appObjectId: app.appId,
    appId: app.id,
    startDate: credential.startDateTime ? new Date(credential.startDateTime) : undefined,
    expires: credential.endDateTime ? new Date(credential.endDateTime) : undefined,
    type: credential.customKeyIdentifier ? CredentialType.Certificate : CredentialType.Secret,
    status: task.status,
    resourceType: app.resourceType!,
    thumbprint: 'thumbprint' in credential ? credential.thumbprint : undefined,
    displayName: credential.displayName,
    comment: task.comment,
    ownerId: task.ownerId,
    logoUrl: app.info?.logoUrl,
    isSaml,
  };
}
